import { apiClient } from '@/lib/api-client';

// ─── Types ──────────────────────────────────────────────────────────────────

export type KdsItemStatus = 'PENDING' | 'PREPARING' | 'READY' | 'SERVED' | 'CANCELLED';

export interface KdsStation {
  id: string;
  tenant_id: string;
  location_id: string;
  name: string;
  station_type: string;
  is_active: boolean;
  is_online: boolean;
  created_at: string;
}

export interface KdsQueueItem {
  id: string;
  order_id: string;
  order_number: string;
  order_item_id: string;
  product_name: string;
  quantity: number;
  modifiers: string[];
  notes: string | null;
  status: KdsItemStatus;
  table_number: string | null;
  order_type: string;
  created_at: string;
  elapsed_seconds: number;
  /**
   * QA-KDS-218 — Coursing. Items in course N are HELD until every item in
   * courses < N is READY/SERVED. Undefined ⇒ single course (never held).
   */
  course?: number;
}

export interface KdsStats {
  station_id: string;
  pending_count: number;
  preparing_count: number;
  avg_preparation_time_seconds: number;
  orders_completed_today: number;
}

// ─── Service ────────────────────────────────────────────────────────────────

export const kdsService = {
  listStations() {
    return apiClient<KdsStation[]>('/kds/stations');
  },

  getStationQueue(stationId: string) {
    return apiClient<KdsQueueItem[]>(`/kds/stations/${stationId}/queue`);
  },

  getAllQueue() {
    return apiClient<KdsQueueItem[]>('/kds/stations/all/queue');
  },

  updateItemStatus(stationId: string, itemId: string, status: KdsItemStatus) {
    return apiClient<KdsQueueItem>(`/kds/stations/${stationId}/items/${itemId}`, {
      method: 'PATCH',
      body: { status },
    });
  },

  getStationStats(stationId: string) {
    return apiClient<KdsStats>(`/kds/stations/${stationId}/stats`);
  },

  bumpAll(stationId: string) {
    return apiClient<{ bumped: number }>(`/kds/stations/${stationId}/bump-all`, {
      method: 'PATCH',
    });
  },

  /**
   * QA-KDS-207 — Manager-override reassignment of an order between stations.
   * Backend enforces MANAGER+ role; cashiers will receive 403.
   */
  reassignOrder(sourceStationId: string, orderId: string, targetStationId: string) {
    return apiClient<{
      order_id: string;
      source_station_id: string;
      target_station_id: string;
      reassigned_count: number;
    }>(`/kds/stations/${sourceStationId}/orders/${orderId}/reassign`, {
      method: 'PATCH',
      body: { target_station_id: targetStationId },
    });
  },

  /**
   * G3.15 — Read-only alert feed: list stations with no active staff.
   * Used by the KDS banner on the main view.
   */
  getUnassignedStations(locationId: string) {
    return apiClient<Array<{ id: string; name: string; station_type: string }>>(
      `/kds/stations/unassigned?location_id=${encodeURIComponent(locationId)}`,
    );
  },
};
